import { z } from "zod";
import { DemoAppointmentStatusSchema, SchedulingAppointmentSourceSchema, SchedulingIntakeReviewSummarySchema, SchedulingIntakeSectionReviewSchema, SchedulingIntakeSectionSchema, } from "./demo-scheduling.js";
import { PatientAdministrativeSexSchema } from "./patient-profile.js";
import { IsoDateTimeSchema } from "./primitives.js";
import { SchedulingAppointmentSchema } from "./scheduling.js";
/**
 * Staff review of a pre-visit questionnaire and applying it to the chart (PA-6 / EXECDEV-214),
 * with no AI anywhere. The read itself is SchedulingIntakeReviewSchema (scheduling.ts), and each
 * appointment's review state is DemoAppointmentSchema.intakeReview. Staff routes, all on the
 * signed-in member's site:
 *
 * - GET  /v1/scheduling/intake-reviews?filter=          SchedulingIntakeReviewListSchema
 * - GET  /v1/scheduling/appointments/:id/intake          SchedulingIntakeReviewSchema
 * - GET  /v1/scheduling/appointments/:id/patient-matches SchedulingPatientMatchesSchema
 * - POST /v1/scheduling/appointments/:id/patient         ReconcileSchedulingPatient*
 * - POST /v1/scheduling/appointments/:id/intake/demographics   ApplyIntakeDemographics*
 * - POST /v1/scheduling/appointments/:id/intake/chart-entries  ApplyIntakeChartEntries*
 * - POST /v1/scheduling/appointments/:id/intake/reason         ApplyIntakeReason*
 * - POST /v1/scheduling/appointments/:id/intake/reviewed       MarkIntakeSectionReviewed*
 *
 * Every apply takes its values from the stored questionnaire, word for word: a request names
 * what to apply, never a value to write (chart entries name parts of the patient's answer, which
 * the server checks). Corrections go through the ordinary chart editors afterwards. Applying
 * never marks a section reviewed; "Done reviewing" does, once per section.
 *
 * Returning-patient recognition (PA-10 / EXECDEV-217) reuses the same match read and reconcile
 * path for a returning patient's online booking before any questionnaire: the match read answers
 * with basis booking, `mismatch` rules the booking's charts out, and `link` may take the chart's
 * date of birth for the booking (correctBookingDateOfBirth). The schedule read carries only each
 * booking's state (DemoAppointmentSchema.patientMatch).
 *
 * This file imports scheduling.ts and never the other way round.
 */
const PositiveVersionSchema = z.number().int().positive();
function unique(key = String) {
    return (values) => new Set(values.map(key)).size === values.length;
}
/**
 * A client's retry key for an apply. The server prefixes it before handing it to the chart
 * writers, which take at most 128 characters, so it is capped at 100 here.
 */
export const SchedulingIntakeIdempotencyKeySchema = z.string().min(8).max(100);
// ---------------------------------------------------------------------------
// Probable charts (one matcher, shared with PA-5 and PA-10)
// ---------------------------------------------------------------------------
/**
 * Why a chart is a probable match. Every match is an active chart in the clinic's own
 * organization. name_and_dob: legal last name, date of birth, and legal first name or preferred
 * name either way round. health_number: the provincial or federal plan number, compared as
 * upper-case letters and digits. dob_and_phone: date of birth and a 10-digit phone number (11
 * with a leading 1).
 */
export const SchedulingChartMatchSignalSchema = z.enum([
    "name_and_dob",
    "health_number",
    "dob_and_phone",
]);
/** At most this many probable charts are returned, strongest first. */
export const SCHEDULING_CHART_MATCH_LIMIT = 10;
export const SchedulingChartMatchSchema = z
    .object({
    patientId: z.string().uuid(),
    mrn: z.string().min(1).max(200),
    displayName: z.string().min(1).max(240),
    dateOfBirth: z.iso.date(),
    administrativeSex: PatientAdministrativeSexSchema.optional(),
    matchedOn: z
        .array(SchedulingChartMatchSignalSchema)
        .min(1)
        .max(3)
        .refine(unique(), "Each signal is listed once"),
    /**
     * Whether "link" may pick it as it stands: its date of birth equals the booking's. A health
     * number on a chart with another date of birth is shown, but cannot be linked as it stands:
     * once staff checked the patient's card, `link` with correctBookingDateOfBirth links it and
     * gives the booking the chart's date of birth (PA-10).
     */
    linkable: z.boolean(),
    /** Staff marked it not this patient ("mismatch"); it no longer holds up creating a chart. */
    dismissed: z.boolean(),
    registeredAt: IsoDateTimeSchema.optional(),
    lastVisitAt: IsoDateTimeSchema.optional(),
})
    .strict();
/**
 * What a returning patient gave when booking online (PA-10), never the card's number.
 * verified_health_card: they passed the existing-patients-only check (health card and date of
 * birth) and several charts matched; a single match links by itself. health_card: they gave the
 * optional health card number at a clinic that books anyone. details: their name, date of birth
 * and phone number only.
 */
export const SchedulingBookingMatchSourceSchema = z.enum([
    "verified_health_card",
    "health_card",
    "details",
]);
/**
 * `GET /v1/scheduling/appointments/:id/patient-matches`: an audited read, fetched on demand,
 * never on the schedule poll.
 */
export const SchedulingPatientMatchesSchema = z
    .object({
    /**
     * Whose details were matched: the submitted questionnaire (intake), what the desk typed for
     * a walk-in before adding it (walk_in: PA-5, WalkInChartMatchesRequest), or a returning
     * patient's online booking before any questionnaire with identity (booking: PA-10). A
     * booking's matches are the charts its health card matched when it was booked (the number is
     * never kept) and the charts its name, date of birth and phone match, with what staff ruled
     * out; a chart with the card but another date of birth is listed, not linkable.
     */
    basis: z.enum(["intake", "booking", "walk_in"]),
    /**
     * Basis booking only: what the patient gave when booking. Absent when that is not known,
     * as for a booking made before PA-10.
     */
    bookedWith: SchedulingBookingMatchSourceSchema.optional(),
    matches: z
        .array(SchedulingChartMatchSchema)
        .max(SCHEDULING_CHART_MATCH_LIMIT)
        .refine(unique((match) => match.patientId), "Each chart is listed once"),
})
    .strict()
    .superRefine((value, context) => {
    if (value.bookedWith !== undefined && value.basis !== "booking") {
        context.addIssue({
            code: "custom",
            message: "Only a booking's matches say what the patient booked with",
            path: ["bookedWith"],
        });
    }
});
// ---------------------------------------------------------------------------
// Reconcile: link, create or mismatch (the booking's one chart path)
// ---------------------------------------------------------------------------
/** The probable charts the reviewer saw and ruled out; ids only. */
const AcknowledgedMatchIdsSchema = z
    .array(z.string().uuid())
    .max(SCHEDULING_CHART_MATCH_LIMIT)
    .refine(unique(), "Each chart is listed once");
/**
 * `POST /v1/scheduling/appointments/:id/patient`.
 *
 * - link: this chart is the patient. Active, in the clinic, same date of birth as the booking,
 *   unless correctBookingDateOfBirth says otherwise. Needs no questionnaire.
 * - create: none of the probable charts is the patient; register a new chart from the
 *   questionnaire (identity required) and link it. acknowledgedMatchIds must include every
 *   current match not already dismissed, or nothing is created (chart_matches_changed). One
 *   chart at most per appointment, however often it is sent.
 * - mismatch: none of the listed charts is the patient, and no chart is created yet. They are
 *   recorded as dismissed (who and when, in the audit trail), under the same guard. On a
 *   returning patient's booking (PA-10) it also rules out the booking's charts before any
 *   questionnaire, and charts ruled out there count as ruled out when a questionnaire's chart is
 *   created later. Ruling out never changes the appointment's version, so the patient's open
 *   link keeps working.
 *
 * A booking that already has a chart is refused (appointment_changed), and so is any change
 * after the patient arrived or the visit closed.
 */
export const ReconcileSchedulingPatientRequestSchema = z.discriminatedUnion("action", [
    z
        .object({
        action: z.literal("link"),
        expectedVersion: PositiveVersionSchema,
        patientId: z.string().uuid(),
        /**
         * The booking's date of birth was typed wrong (PA-10, lead decision I4): staff checked the
         * patient's health card, or asked them, and the booking takes the chart's date of birth as
         * it is linked, in one step, so the desk never has to create a duplicate chart. Only before
         * arrival. Audited as its own event (scheduling.booking_date_of_birth_corrected) without
         * either date; the patient's links then ask for the chart's date. With dates already equal
         * it is a plain link. Clients send it only after the member confirmed the correction.
         */
        correctBookingDateOfBirth: z.literal(true).optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal("create"),
        expectedVersion: PositiveVersionSchema,
        acknowledgedMatchIds: AcknowledgedMatchIdsSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal("mismatch"),
        expectedVersion: PositiveVersionSchema,
        acknowledgedMatchIds: AcknowledgedMatchIdsSchema.min(1),
    })
        .strict(),
]);
export const ReconcileSchedulingPatientResponseSchema = z.discriminatedUnion("outcome", [
    z
        .object({
        outcome: z.literal("linked"),
        appointment: SchedulingAppointmentSchema,
        patientId: z.string().uuid(),
    })
        .strict(),
    z
        .object({
        outcome: z.literal("created"),
        appointment: SchedulingAppointmentSchema,
        patientId: z.string().uuid(),
        mrn: z.string().min(1).max(200),
    })
        .strict(),
    z
        .object({
        outcome: z.literal("mismatch"),
        appointment: SchedulingAppointmentSchema,
    })
        .strict(),
]);
// ---------------------------------------------------------------------------
// Administrative apply: identity and coverage onto the linked chart
// ---------------------------------------------------------------------------
/** Identity answers staff may apply. Never the date of birth: a linked chart already has it. */
export const INTAKE_IDENTITY_FIELDS = [
    "legalFirstName",
    "legalMiddleName",
    "legalLastName",
    "preferredName",
    "administrativeSex",
    "phone",
    "email",
    "address",
    "emergencyContact",
];
/** Every answer the demographics apply takes: identity plus coverage. */
export const INTAKE_DEMOGRAPHIC_FIELDS = [...INTAKE_IDENTITY_FIELDS, "coverage"];
export const IntakeDemographicFieldSchema = z.enum(INTAKE_DEMOGRAPHIC_FIELDS);
/**
 * `POST .../intake/demographics`: copy the chosen answers onto the linked chart's
 * demographics, as the patient gave them. Address, emergency contact and coverage replace the
 * chart's whole value (no coverage, "cash", ends every current plan). An Ontario card with the
 * chart's own number only updates its version code and expiry. A chosen field the patient left
 * blank is refused, and so is any identity field when the questionnaire did not ask for identity
 * (intake_identity_missing).
 */
export const ApplyIntakeDemographicsRequestSchema = z
    .object({
    fields: z
        .array(IntakeDemographicFieldSchema)
        .min(1)
        .max(INTAKE_DEMOGRAPHIC_FIELDS.length)
        .refine(unique(), "Each field is listed once"),
    /** The chart demographics version the reviewer compared with (chart_changed when stale). */
    expectedPatientVersion: PositiveVersionSchema,
    idempotencyKey: SchedulingIntakeIdempotencyKeySchema,
})
    .strict();
export const ApplyIntakeDemographicsResponseSchema = z
    .object({
    applicationId: z.string().uuid(),
    /** True when this key was applied before: nothing changed again. */
    replayed: z.boolean(),
    /** The demographics fields the chart writer changed; values already equal are skipped. */
    changedFields: z.array(z.string().min(1).max(80)).max(20),
})
    .strict();
// ---------------------------------------------------------------------------
// Clinical apply: allergies, medications and history (physicians and nurses)
// ---------------------------------------------------------------------------
export const IntakeChartEntrySectionSchema = z.enum(["allergies", "medications", "pmh"]);
/** The answer each chart section takes its entries from. */
export const INTAKE_CHART_ENTRY_ANSWER = {
    allergies: "allergies",
    medications: "medications",
    pmh: "medicalHistory",
};
/** A chart entry's name is at most this long (the profile writer's display limit). */
export const INTAKE_CHART_ENTRY_MAX_LENGTH = 240;
/** At most this many entries in one apply. */
export const INTAKE_CHART_ENTRIES_LIMIT = 50;
/** One entry: a part of the patient's answer, word for word (splitIntakeAnswer). */
export const IntakeChartEntrySchema = z
    .object({ display: z.string().trim().min(1).max(INTAKE_CHART_ENTRY_MAX_LENGTH) })
    .strict();
/**
 * `POST .../intake/chart-entries`: add the chosen parts of the patient's answer to the chart as
 * new patient-reported entries (active), marked as from the patient intake. Each display must
 * appear word for word in that section's answer (intakeAnswerHasPart). An entry with the name of
 * one already on the chart (any status but removed) is not added again; a removed (inactive) entry
 * of that name is made active again as patient-reported. Nothing else on the chart is rewritten:
 * if it would be, nothing is added and staff use the chart editor. Physicians and nurses only.
 */
export const ApplyIntakeChartEntriesRequestSchema = z
    .object({
    section: IntakeChartEntrySectionSchema,
    entries: z
        .array(IntakeChartEntrySchema)
        .min(1)
        .max(INTAKE_CHART_ENTRIES_LIMIT)
        .refine(unique((entry) => entry.display.toLowerCase()), "Each entry is listed once"),
    idempotencyKey: SchedulingIntakeIdempotencyKeySchema,
})
    .strict();
export const ApplyIntakeChartEntriesResponseSchema = z
    .object({
    applicationId: z.string().uuid(),
    section: IntakeChartEntrySectionSchema,
    /** True when this key was applied before: nothing was added again. */
    replayed: z.boolean(),
    /** Entries now on the chart from this apply: new ones and removed ones made active again. */
    added: z.number().int().min(0).max(INTAKE_CHART_ENTRIES_LIMIT),
    /** Requested entries the chart already had under the same name (not removed), so not added. */
    alreadyOnChart: z
        .array(z.string().min(1).max(INTAKE_CHART_ENTRY_MAX_LENGTH))
        .max(INTAKE_CHART_ENTRIES_LIMIT),
})
    .strict();
/**
 * Answers that mean "nothing to add". Skipped, never turned into "no known allergies": an
 * empty allergy list and a recorded "no known allergies" are different facts.
 */
const NONE_ANSWERS = new Set([
    "none",
    "no",
    "nil",
    "n/a",
    "na",
    "nka",
    "nkda",
    "no known allergies",
    "no known drug allergies",
    "not applicable",
    "nothing",
]);
const LIST_MARKER = /^(?:[-*•·]|\d{1,2}[.)])\s+/u;
/**
 * Cuts an answer at line breaks and semicolons, deterministically and without guessing: each
 * part is trimmed and loses a leading list marker ("-", "*", "1."), and blank parts are dropped.
 * Commas stay, since "metformin 500 mg, twice daily" is one medication.
 */
export function splitIntakeAnswer(text) {
    const entries = [];
    const tooLong = [];
    const noneAnswers = [];
    const seen = new Set();
    for (const raw of text.split(/\r\n|[\r\n;]/u)) {
        const part = raw.trim().replace(LIST_MARKER, "").trim();
        if (part === "")
            continue;
        const key = part.toLowerCase();
        if (NONE_ANSWERS.has(key.replace(/[.!]+$/u, ""))) {
            noneAnswers.push(part);
        }
        else if (part.length > INTAKE_CHART_ENTRY_MAX_LENGTH) {
            tooLong.push(part);
        }
        else if (!seen.has(key)) {
            seen.add(key);
            entries.push(part);
        }
    }
    return { entries, tooLong, noneAnswers };
}
/** The server's rule for a chart entry: its trimmed name appears in the answer word for word. */
export function intakeAnswerHasPart(answer, display) {
    const part = display.trim();
    return part !== "" && answer.includes(part);
}
// ---------------------------------------------------------------------------
// Reason: the booking reason before arrival
// ---------------------------------------------------------------------------
/** The longest booking reason (DemoAppointmentSchema.reason). */
export const SCHEDULING_BOOKING_REASON_MAX_LENGTH = 240;
/** Whether the patient's reason fits a booking reason as it is. */
export function intakeReasonFitsBooking(reason) {
    const trimmed = reason.trim();
    return trimmed.length >= 1 && trimmed.length <= SCHEDULING_BOOKING_REASON_MAX_LENGTH;
}
/**
 * `POST .../intake/reason`: make the patient's reason, word for word, the booking reason, which
 * check-in copies into the chief complaint. Only before arrival (booked or confirmed); a reason
 * over 240 characters is refused (intake_reason_too_long) and staff edit the booking instead.
 */
export const ApplyIntakeReasonRequestSchema = z
    .object({ expectedVersion: PositiveVersionSchema })
    .strict();
export const ApplyIntakeReasonResponseSchema = z
    .object({
    applicationId: z.string().uuid(),
    appointment: SchedulingAppointmentSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Done reviewing (per section)
// ---------------------------------------------------------------------------
/**
 * `POST .../intake/reviewed`: mark one section reviewed. Clinical needs a physician or nurse.
 * Refused once the review has closed (intake_review_closed). The first reviewer is kept, and the
 * appointment's version does not change, so a patient's open link keeps working.
 */
export const MarkIntakeSectionReviewedRequestSchema = z
    .object({ section: SchedulingIntakeSectionSchema })
    .strict();
export const MarkIntakeSectionReviewedResponseSchema = z
    .object({
    section: SchedulingIntakeSectionSchema,
    /** False when someone had already marked it; their name and time are kept. */
    marked: z.boolean(),
    review: SchedulingIntakeSectionReviewSchema,
})
    .strict()
    .refine((value) => value.review.state === "reviewed", {
    message: "A marked section is reviewed",
    path: ["review", "state"],
});
// ---------------------------------------------------------------------------
// Questionnaires still to review, across every date
// ---------------------------------------------------------------------------
/**
 * to_review: open reviews with a section the viewer reviews still awaiting review.
 * open: every open review.
 */
export const SchedulingIntakeReviewListFilterSchema = z.enum(["to_review", "open"]);
/** `GET /v1/scheduling/intake-reviews` query. */
export const SchedulingIntakeReviewListQuerySchema = z
    .object({ filter: SchedulingIntakeReviewListFilterSchema.default("to_review") })
    .strict();
/** At most this many listed, earliest visit first. */
export const SCHEDULING_INTAKE_REVIEW_LIST_LIMIT = 200;
export const SchedulingIntakeReviewListItemSchema = z
    .object({
    appointmentId: z.string().uuid(),
    /** Present once the patient has arrived (joins Today's board rows). */
    encounterId: z.string().uuid().optional(),
    status: DemoAppointmentStatusSchema,
    /** walk_in for a walk-in's questionnaire (PA-5); its startAt is the arrival. */
    source: SchedulingAppointmentSourceSchema,
    startAt: IsoDateTimeSchema,
    patientDisplayName: z.string().trim().min(1).max(240),
    chartLinked: z.boolean(),
    intakeReview: SchedulingIntakeReviewSummarySchema,
})
    .strict();
export const SchedulingIntakeReviewListSchema = z
    .object({
    items: z.array(SchedulingIntakeReviewListItemSchema).max(SCHEDULING_INTAKE_REVIEW_LIST_LIMIT),
    /** More matched than are listed. */
    truncated: z.boolean(),
})
    .strict();
//# sourceMappingURL=scheduling-intake.js.map